/**
 * Template Service
 * Phase 2 Sprint 13: Template Infrastructure
 * 
 * Provides CRUD operations for template management
 * Custom templates are now persisted to PostgreSQL backend with IDB fallback
 */

import { db } from '../idb/chunk-db';
import { 
  Template, 
  TemplateCategory, 
  TemplateFilter,
  TemplateValidationResult 
} from '../../../types/template';
import { TEMPLATE_SEEDS } from '../../../data/templateSeeds';
import logger from '../utils/logger';
import api from './apiClient';

class TemplateService {
  private seeded = false;

  /**
   * Get all templates
   */
  async getAllTemplates(): Promise<Template[]> {
    try {
      let templates = await db.templates.toArray();
      
      // Seed templates if empty
      if (templates.length === 0) {
        await this.seedTemplates();
        return await db.templates.toArray();
      }
      
      // Check if official templates need to be reseeded (missing fields or aspectRatio mismatch)
      const officialTemplates = templates.filter(t => t.isOfficial);
      const needsReseed = officialTemplates.some(t => {
        const seed = TEMPLATE_SEEDS.find(s => s.id === t.id);
        // Reseed if customization is in seed but missing from stored template
        const missingCustomization = seed?.customization && !t.customization;
        // Reseed if backgroundImage is in seed but missing from stored template
        const missingBackgroundImage = seed?.backgroundImage && !t.backgroundImage;
        // Reseed if aspectRatio in seed differs from stored template
        const aspectRatioMismatch = seed?.aspectRatio && t.aspectRatio !== seed.aspectRatio;
        return missingCustomization || missingBackgroundImage || aspectRatioMismatch;
      });
      
      if (needsReseed) {
        logger.info('Official templates need reseed (field mismatch detected)');
        await this.reseedOfficialTemplates();
        templates = await db.templates.toArray();
      }
      
      return templates.sort((a, b) => 
        (b.createdAt || 0) - (a.createdAt || 0)
      );
    } catch (error) {
      logger.error('Failed to get templates:', error);
      throw new Error('Failed to load templates');
    }
  }

  /**
   * Get template by ID
   */
  async getTemplateById(id: string): Promise<Template | undefined> {
    try {
      return await db.templates.get(id);
    } catch (error) {
      logger.error(`Failed to get template ${id}:`, error);
      return undefined;
    }
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category: TemplateCategory): Promise<Template[]> {
    try {
      return await db.templates
        .where('category')
        .equals(category)
        .sortBy('createdAt');
    } catch (error) {
      logger.error(`Failed to get templates for category ${category}:`, error);
      return [];
    }
  }

  /**
   * Filter templates
   */
  async filterTemplates(filter: TemplateFilter): Promise<Template[]> {
    try {
      let query = db.templates.toCollection();
      
      if (filter.category && filter.category !== 'all') {
        query = db.templates.where('category').equals(filter.category);
      }
      
      if (filter.isOfficial !== undefined) {
        query = query.filter(t => t.isOfficial === filter.isOfficial);
      }
      
      if (filter.createdBy) {
        query = query.filter(t => t.createdBy === filter.createdBy);
      }
      
      let templates = await query.toArray();
      
      // Search by query if provided
      if (filter.searchQuery) {
        const searchLower = filter.searchQuery.toLowerCase();
        templates = templates.filter(t => 
          t.title.toLowerCase().includes(searchLower) ||
          t.description.toLowerCase().includes(searchLower) ||
          t.category.toLowerCase().includes(searchLower)
        );
      }
      
      return templates.sort((a, b) => 
        (b.createdAt || 0) - (a.createdAt || 0)
      );
    } catch (error) {
      logger.error('Failed to filter templates:', error);
      return [];
    }
  }

  /**
   * Create or update template
   */
  async saveTemplate(template: Template): Promise<void> {
    try {
      const validation = this.validateTemplate(template);
      if (!validation.valid) {
        throw new Error(`Template validation failed: ${validation.errors.join(', ')}`);
      }
      
      await db.templates.put(template);
      logger.info(`Template saved: ${template.id}`);
    } catch (error) {
      logger.error('Failed to save template:', error);
      throw error;
    }
  }

  /**
   * Delete template
   */
  async deleteTemplate(id: string): Promise<void> {
    try {
      await db.templates.delete(id);
      logger.info(`Template deleted: ${id}`);
    } catch (error) {
      logger.error('Failed to delete template:', error);
      throw error;
    }
  }

  /**
   * Seed initial templates
   */
  async seedTemplates(): Promise<void> {
    if (this.seeded) {
      logger.info('Templates already seeded, skipping');
      return;
    }
    
    try {
      const existingTemplates = await db.templates.toArray();
      
      // Only seed if no templates exist
      if (existingTemplates.length > 0) {
        logger.info(`Templates already exist (${existingTemplates.length}), skipping seed`);
        this.seeded = true;
        return;
      }
      
      logger.info(`Seeding ${TEMPLATE_SEEDS.length} templates...`);
      
      // Validate all templates before seeding
      const validTemplates: Template[] = [];
      const errors: string[] = [];
      
      for (const template of TEMPLATE_SEEDS) {
        const validation = this.validateTemplate(template);
        if (validation.valid) {
          validTemplates.push(template);
        } else {
          errors.push(`${template.id}: ${validation.errors.join(', ')}`);
        }
      }
      
      if (errors.length > 0) {
        logger.warn('Template validation errors:', errors);
      }
      
      if (validTemplates.length === 0) {
        throw new Error('No valid templates to seed');
      }
      
      // Bulk insert templates
      await db.templates.bulkAdd(validTemplates);
      
      this.seeded = true;
      logger.info(`Successfully seeded ${validTemplates.length} templates`);
    } catch (error) {
      logger.error('Failed to seed templates:', error);
      throw new Error('Template seeding failed');
    }
  }

  /**
   * Force re-seed official templates (useful after template updates)
   */
  async reseedOfficialTemplates(): Promise<void> {
    try {
      logger.info('Re-seeding official templates...');
      
      // Delete all official templates
      const officialTemplates = await db.templates
        .filter(t => t.isOfficial === true)
        .toArray();
      
      const idsToDelete = officialTemplates.map(t => t.id);
      await db.templates.bulkDelete(idsToDelete);
      
      logger.info(`Deleted ${idsToDelete.length} official templates`);
      
      // Validate all templates before seeding
      const validTemplates: Template[] = [];
      const errors: string[] = [];
      
      for (const template of TEMPLATE_SEEDS) {
        const validation = this.validateTemplate(template);
        if (validation.valid) {
          validTemplates.push(template);
        } else {
          errors.push(`${template.id}: ${validation.errors.join(', ')}`);
        }
      }
      
      if (errors.length > 0) {
        logger.warn('Template validation errors:', errors);
      }
      
      if (validTemplates.length === 0) {
        throw new Error('No valid templates to reseed');
      }
      
      // Bulk insert fresh templates
      await db.templates.bulkAdd(validTemplates);
      
      this.seeded = true;
      logger.info(`Successfully reseeded ${validTemplates.length} official templates`);
    } catch (error) {
      logger.error('Failed to reseed templates:', error);
      throw new Error('Template reseeding failed');
    }
  }

  /**
   * Validate template structure
   */
  validateTemplate(template: Template): TemplateValidationResult {
    const errors: string[] = [];
    
    // Required fields
    if (!template.id) errors.push('Missing template ID');
    if (!template.title) errors.push('Missing template title');
    if (!template.category) errors.push('Missing template category');
    if (!template.createdBy) errors.push('Missing createdBy field');
    
    // Scenes validation
    if (!template.scenes || template.scenes.length === 0) {
      errors.push('Template must have at least one scene');
    } else {
      template.scenes.forEach((scene, index) => {
        if (scene.order !== index) {
          errors.push(`Scene ${index}: order mismatch (expected ${index}, got ${scene.order})`);
        }
        if (!scene.title) {
          errors.push(`Scene ${index}: missing title`);
        }
        if (!scene.layoutPreset) {
          errors.push(`Scene ${index}: missing layoutPreset`);
        }
        if (!scene.scriptKey) {
          errors.push(`Scene ${index}: missing scriptKey`);
        }
        if (typeof scene.suggestedDuration !== 'number' || scene.suggestedDuration <= 0) {
          errors.push(`Scene ${index}: invalid suggestedDuration`);
        }
      });
    }
    
    // Scripts validation
    if (!template.scripts || template.scripts.length === 0) {
      errors.push('Template must have at least one script');
    } else {
      const scriptKeys = new Set(template.scripts.map(s => s.key));
      const usedScriptKeys = template.scenes.map(s => s.scriptKey);
      
      // Check if all scene scriptKeys exist in scripts
      usedScriptKeys.forEach(key => {
        if (!scriptKeys.has(key)) {
          errors.push(`Scene references non-existent script key: ${key}`);
        }
      });
      
      // Validate script structure
      template.scripts.forEach((script, index) => {
        if (!script.key) {
          errors.push(`Script ${index}: missing key`);
        }
        if (!script.content) {
          errors.push(`Script ${index}: missing content`);
        }
      });
    }
    
    // Example output validation
    if (!template.exampleOutput) {
      errors.push('Missing exampleOutput');
    } else {
      if (!template.exampleOutput.title) {
        errors.push('exampleOutput: missing title');
      }
      if (typeof template.exampleOutput.duration !== 'number') {
        errors.push('exampleOutput: invalid duration');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get template statistics
   */
  async getTemplateStats() {
    try {
      const templates = await db.templates.toArray();
      const stats = {
        total: templates.length,
        official: templates.filter(t => t.isOfficial).length,
        custom: templates.filter(t => !t.isOfficial).length,
        byCategory: {} as Record<TemplateCategory, number>
      };
      
      templates.forEach(t => {
        if (!stats.byCategory[t.category]) {
          stats.byCategory[t.category] = 0;
        }
        stats.byCategory[t.category]++;
      });
      
      return stats;
    } catch (error) {
      logger.error('Failed to get template stats:', error);
      return { total: 0, official: 0, custom: 0, byCategory: {} };
    }
  }

  /**
   * Clear all templates (for testing/reset)
   */
  async clearTemplates(): Promise<void> {
    try {
      await db.templates.clear();
      this.seeded = false;
      logger.info('All templates cleared');
    } catch (error) {
      logger.error('Failed to clear templates:', error);
      throw error;
    }
  }

  /**
   * Sprint 19: Custom Template CRUD Operations
   * Now uses backend API with IDB fallback for cross-device persistence
   */

  private backendAvailable = true;

  /**
   * Get all custom templates from backend (with IDB fallback)
   */
  async getCustomTemplates(): Promise<Template[]> {
    try {
      // Try backend first
      if (this.backendAvailable) {
        try {
          const response = await api.get('/api/custom-templates');
          const backendTemplates = response.data.templates || [];
          
          // Convert backend format to frontend Template format
          const templates = backendTemplates.map((t: any) => this.convertBackendTemplate(t));
          
          // Sync to IDB for offline access
          await this.syncToIDB(templates);
          
          logger.info(`Fetched ${templates.length} custom templates from backend`);
          return templates;
        } catch (backendError) {
          logger.warn('Backend unavailable, falling back to IDB:', backendError);
          this.backendAvailable = false;
        }
      }
      
      // Fallback to IDB
      return await db.customTemplates.toArray();
    } catch (error) {
      logger.error('Failed to get custom templates:', error);
      return [];
    }
  }

  /**
   * Create a custom template (persisted to backend + IDB)
   */
  async createCustomTemplate(template: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const customTemplate: Template = {
        ...template,
        id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        isOfficial: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      // Try backend first (userId derived from auth token on server)
      if (this.backendAvailable) {
        try {
          const response = await api.post('/api/custom-templates', customTemplate);
          
          const savedTemplate = response.data.template;
          customTemplate.id = savedTemplate.id;
          
          logger.info(`Custom template created in backend: ${customTemplate.id}`);
        } catch (backendError) {
          logger.warn('Backend save failed, saving to IDB only:', backendError);
          this.backendAvailable = false;
        }
      }

      // Always save to IDB
      await db.customTemplates.add(customTemplate);
      logger.info(`Custom template saved to IDB: ${customTemplate.id}`);
      
      return customTemplate.id;
    } catch (error) {
      logger.error('Failed to create custom template:', error);
      throw new Error('Failed to create custom template');
    }
  }

  /**
   * Update a custom template (persisted to backend + IDB)
   */
  async updateCustomTemplate(id: string, updates: Partial<Template>): Promise<void> {
    try {
      const template = await db.customTemplates.get(id);
      if (!template) {
        throw new Error(`Custom template not found: ${id}`);
      }

      const updatedAt = Date.now();

      // Try backend first
      if (this.backendAvailable) {
        try {
          await api.put(`/api/custom-templates/${id}`, {
            ...updates,
          });
          logger.info(`Custom template updated in backend: ${id}`);
        } catch (backendError) {
          logger.warn('Backend update failed, updating IDB only:', backendError);
          this.backendAvailable = false;
        }
      }

      // Always update IDB
      await db.customTemplates.update(id, {
        ...updates,
        updatedAt,
      });

      logger.info(`Custom template updated: ${id}`);
    } catch (error) {
      logger.error(`Failed to update custom template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a custom template (from backend + IDB)
   */
  async deleteCustomTemplate(id: string): Promise<void> {
    try {
      // Try backend first
      if (this.backendAvailable) {
        try {
          await api.delete(`/api/custom-templates/${id}`);
          logger.info(`Custom template deleted from backend: ${id}`);
        } catch (backendError) {
          logger.warn('Backend delete failed, deleting from IDB only:', backendError);
          this.backendAvailable = false;
        }
      }

      // Always delete from IDB
      await db.customTemplates.delete(id);
      logger.info(`Custom template deleted: ${id}`);
    } catch (error) {
      logger.error(`Failed to delete custom template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Duplicate a template as custom
   */
  async duplicateAsCustom(templateId: string, newTitle?: string): Promise<string> {
    try {
      // Try to get from official templates first
      let sourceTemplate = await db.templates.get(templateId);
      
      // If not found, try custom templates
      if (!sourceTemplate) {
        sourceTemplate = await db.customTemplates.get(templateId);
      }

      if (!sourceTemplate) {
        throw new Error(`Template not found: ${templateId}`);
      }

      const customTemplate: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
        ...sourceTemplate,
        title: newTitle || `${sourceTemplate.title} (Copy)`,
        isOfficial: false,
      };

      return await this.createCustomTemplate(customTemplate);
    } catch (error) {
      logger.error(`Failed to duplicate template ${templateId}:`, error);
      throw error;
    }
  }

  /**
   * Export template as JSON
   */
  async exportTemplateAsJSON(templateId: string): Promise<string> {
    try {
      let template = await db.templates.get(templateId);
      if (!template) {
        template = await db.customTemplates.get(templateId);
      }

      if (!template) {
        throw new Error(`Template not found: ${templateId}`);
      }

      return JSON.stringify(template, null, 2);
    } catch (error) {
      logger.error(`Failed to export template ${templateId}:`, error);
      throw error;
    }
  }

  /**
   * Import template from JSON
   */
  async importTemplateFromJSON(jsonString: string): Promise<string> {
    try {
      const template = JSON.parse(jsonString) as Template;
      
      // Validate the imported template
      const validation = this.validateTemplate(template);
      if (!validation.valid) {
        throw new Error(`Invalid template: ${validation.errors.join(', ')}`);
      }

      // Remove ID and timestamps, let createCustomTemplate handle them
      const { id, createdAt, updatedAt, ...templateData } = template;
      
      return await this.createCustomTemplate({
        ...templateData,
        isOfficial: false,
      });
    } catch (error) {
      logger.error('Failed to import template:', error);
      throw error;
    }
  }

  /**
   * Sync IDB templates to backend (called on app load for migration)
   */
  async syncIDBToBackend(): Promise<{ synced: number; errors: number }> {
    try {
      const idbTemplates = await db.customTemplates.toArray();
      
      if (idbTemplates.length === 0) {
        logger.info('No IDB templates to sync');
        return { synced: 0, errors: 0 };
      }

      try {
        // userId derived from auth token on server
        const response = await api.post('/api/custom-templates/sync', {
          templates: idbTemplates,
        });

        const result = response.data;
        logger.info(`Synced ${result.synced} templates to backend, ${result.errors} errors`);
        
        this.backendAvailable = true;
        return { synced: result.synced, errors: result.errors };
      } catch (backendError) {
        logger.warn('Backend sync failed:', backendError);
        this.backendAvailable = false;
        return { synced: 0, errors: idbTemplates.length };
      }
    } catch (error) {
      logger.error('Failed to sync IDB templates to backend:', error);
      return { synced: 0, errors: 0 };
    }
  }

  /**
   * Convert backend template format to frontend Template format
   */
  private convertBackendTemplate(backendTemplate: any): Template {
    return {
      id: backendTemplate.id,
      title: backendTemplate.title,
      description: backendTemplate.description,
      category: backendTemplate.category,
      aspectRatio: backendTemplate.aspectRatio || '16:9',
      scenes: backendTemplate.scenes || [],
      scripts: backendTemplate.scripts || [],
      exampleOutput: backendTemplate.exampleOutput || { title: '', duration: 0, whatUserGets: '' },
      thumbnailUrl: backendTemplate.thumbnailUrl,
      backgroundImage: backendTemplate.backgroundImage,
      createdBy: backendTemplate.userId || 'anonymous',
      isOfficial: false,
      createdAt: backendTemplate.createdAt ? new Date(backendTemplate.createdAt).getTime() : Date.now(),
      updatedAt: backendTemplate.updatedAt ? new Date(backendTemplate.updatedAt).getTime() : Date.now(),
      customization: backendTemplate.customization,
    };
  }

  /**
   * Sync templates from backend to IDB for offline access
   */
  private async syncToIDB(templates: Template[]): Promise<void> {
    try {
      // Clear existing IDB custom templates and replace with backend data
      await db.customTemplates.clear();
      if (templates.length > 0) {
        await db.customTemplates.bulkAdd(templates);
      }
      logger.info(`Synced ${templates.length} templates to IDB`);
    } catch (error) {
      logger.error('Failed to sync templates to IDB:', error);
    }
  }
}

export const templateService = new TemplateService();
export default templateService;
